import * as SpeechSDK from "microsoft-cognitiveservices-speech-sdk";
import { debugLog, debugWarn, debugError } from "../common/utils/debug";
import { SOCKET_EVENTS } from "../common/constants/app.constants";
import {
  AZURE_AVATAR_CONFIG,
  AVATAR_ELEMENT_IDS,
  AVATAR_CSS_CLASSES,
  AVATAR_STATUS_TYPES,
  WEBRTC_CONFIG,
} from "../common/constants/avatar.constants";
import {
  generateSSML,
  createStatusOverlayHTML,
  configureVideoElement,
  getAzureAvatarConfigFromEnv,
  unmuteAllAudioElements,
} from "../common/utils/avatar.utils";

declare global {
  interface Window {
    SpeechSDK: any;
  }
}

interface ICEServerConfig {
  Urls: string[];
  Username: string;
  Password: string;
}

interface AzureAvatarConfig {
  subscriptionKey: string;
  serviceRegion: string;
  voiceName: string;
  avatarCharacter: string;
  avatarStyle: string;
  customized?: boolean;
  useBuiltInVoice?: boolean;
  voiceSpeed?: number;
  deploymentId?: string;
  endpoint?: string;
}

/**
 * Check if custom endpoint mode is enabled via REACT_APP_AVATAR_FROM_ENDPOINT env var
 * Returns true only if the env var is set to 'true' (case-insensitive, trimmed)
 */
const isFromEndpointEnabled = (): boolean => {
  const rawValue = process.env.REACT_APP_AVATAR_FROM_ENDPOINT;
  const envValue = rawValue?.trim().toLowerCase();
  const result = envValue === 'true';
  debugLog(`🔍 [DEBUG] REACT_APP_AVATAR_FROM_ENDPOINT: raw="${rawValue}", trimmed="${envValue}", result=${result}`);
  return result;
};

/**
 * Create SpeechConfig either from custom endpoint or subscription based on configuration
 * When REACT_APP_AVATAR_FROM_ENDPOINT=true and endpoint is provided, uses fromEndpoint()
 * Otherwise falls back to fromSubscription() with region
 */
const createSpeechConfig = (config: AzureAvatarConfig): SpeechSDK.SpeechConfig => {
  const useFromEndpoint = isFromEndpointEnabled();
  const hasEndpoint = !!config.endpoint;

  debugLog(`🔍 [DEBUG] createSpeechConfig: useFromEndpoint=${useFromEndpoint}, hasEndpoint=${hasEndpoint}, endpoint="${config.endpoint}"`);

  if (useFromEndpoint && config.endpoint) {
    debugLog(`🌐 Using SpeechConfig.fromEndpoint with custom endpoint: ${config.endpoint}`);
    return SpeechSDK.SpeechConfig.fromEndpoint(
      new URL(config.endpoint),
      config.subscriptionKey
    );
  } else {
    debugLog(`🌐 Using SpeechConfig.fromSubscription with region: ${config.serviceRegion}`);
    return SpeechSDK.SpeechConfig.fromSubscription(
      config.subscriptionKey,
      config.serviceRegion
    );
  }
};

/**
 * Azure Custom Avatar Service
 * Manages Microsoft Azure Custom Avatar integration with WebRTC for real-time speech and animation
 */
class AzureCustomAvatarService {
  private avatarSynthesizer: SpeechSDK.AvatarSynthesizer | null = null;
  private peerConnection: RTCPeerConnection | null = null;
  private avatarElement: HTMLElement | null = null;
  private avatarSessionId: string | null = null;
  private isReady: boolean = false;
  private isConnected: boolean = false;
  private reconnectionTimeout: NodeJS.Timeout | null = null;
  private isRecovering: boolean = false;
  private configuredVoiceName: string = AZURE_AVATAR_CONFIG.DEFAULT_VOICE_NAME;
  private configuredVoiceSpeed: number = 1.0;
  private isPaused: boolean = false;
  private pausedText: string = "";
  private externalConfig: AzureAvatarConfig | null = null;
  private speechQueue: Array<{ text: string; emotion?: string }> = [];
  private currentlySpeaking: boolean = false;
  private healthMonitorInterval: NodeJS.Timeout | null = null;
  private lastVideoTime: number = 0;
  private isAudioUnmuted: boolean = false;

  // TCP fallback state
  private currentIceConfig: ICEServerConfig | null = null;
  private tcpFallbackAttempted: boolean = false;
  private iceConnectionAttemptStart: number = 0;

  /**
   * Initialize Azure Custom Avatar
   */
  async initialize(sessionId: string, config?: AzureAvatarConfig): Promise<void> {
    try {
      if (this.avatarSynthesizer || this.peerConnection) {
        await this.stopAvatarSession();
        await new Promise((resolve) =>
          setTimeout(resolve, AZURE_AVATAR_CONFIG.CLEANUP_DELAY_MS)
        );
      }

      this.avatarSessionId = sessionId;
      this.externalConfig = config || null;
      
      // Store voice speed from config
      if (config?.voiceSpeed !== undefined) {
        this.configuredVoiceSpeed = config.voiceSpeed;
      }

      // Set up internal avatar-idle listener to process speech queue
      // This is separate from session-specific handlers
      const handleInternalAvatarIdle = () => {
        debugLog("📡 [AvatarService] Internal: Received avatar-idle event, processing queue");
        this.processNextInQueue();
      };

      // Remove any existing listener first
      window.removeEventListener('avatar-idle-internal', handleInternalAvatarIdle);
      window.addEventListener('avatar-idle-internal', handleInternalAvatarIdle);

      // Set up TCP fallback listener for VPN/firewall issues
      const handleTcpFallback = async (event: Event) => {
        const customEvent = event as CustomEvent;
        const iceConfig = customEvent.detail?.iceConfig as ICEServerConfig;

        if (iceConfig) {
          debugLog("🔄 [AvatarService] TCP Fallback triggered - reinitializing with TCP-only...");
          await this.retryWithTcpFallback(iceConfig);
        }
      };

      window.removeEventListener('avatar-ice-failed-try-tcp', handleTcpFallback);
      window.addEventListener('avatar-ice-failed-try-tcp', handleTcpFallback);

      await this.waitForAvatarContainer();
      await this.startAvatarSession();
    } catch (error) {
      this.showStatus(
        `Initialization failed: ${error}`,
        AVATAR_STATUS_TYPES.ERROR
      );
      throw error;
    }
  }

  /**
   * Wait for avatar container to be available in the DOM
   */
  private async waitForAvatarContainer(): Promise<void> {
    return new Promise((resolve, reject) => {
      let attempts = 0;

      const checkForContainer = () => {
        attempts++;

        // Try to find existing avatar video element
        this.avatarElement = document.getElementById(
          AVATAR_ELEMENT_IDS.AVATAR_VIDEO
        );

        if (!this.avatarElement) {
          // Try to find the avatar canvas parent as fallback
          const avatarCanvas = document.getElementById(
            AVATAR_ELEMENT_IDS.AVATAR_CANVAS
          );
          if (avatarCanvas && avatarCanvas.parentElement) {
            this.avatarElement = avatarCanvas.parentElement;
          }
        } else {
          // Use the parent of the video element as our container
          this.avatarElement =
            this.avatarElement.parentElement || this.avatarElement;
        }

        // Try to find by class name as fallback
        if (!this.avatarElement) {
          const videoByClass = document.querySelector(
            `.${AVATAR_CSS_CLASSES.AVATAR_VIDEO}`
          ) as HTMLElement;
          if (videoByClass) {
            this.avatarElement =
              (videoByClass.parentElement as HTMLElement) || videoByClass;
          }
        }

        if (this.avatarElement) {
          resolve();
          return;
        }

        if (attempts >= AZURE_AVATAR_CONFIG.MAX_CONTAINER_WAIT_ATTEMPTS) {
          reject(
            new Error(
              "Avatar container not found. Make sure the Avatar component is rendered and visible."
            )
          );
          return;
        }

        setTimeout(
          checkForContainer,
          AZURE_AVATAR_CONFIG.CONTAINER_CHECK_INTERVAL_MS
        );
      };

      checkForContainer();
    });
  }

  /**
   * List available avatars for debugging
   */
  private async listAvailableAvatars(): Promise<void> {
    try {
      const config = await getAzureAvatarConfigFromEnv();
      const tempConfig = createSpeechConfig(config);
      const tempSynthesizer = new SpeechSDK.SpeechSynthesizer(tempConfig);
      tempSynthesizer.close();
    } catch (error) {
      throw error;
    }
  }

  /**
   * Start avatar session
   */
  private async startAvatarSession(): Promise<void> {
    try {
      // Use external config from backend if provided, otherwise load from env
      const config = this.externalConfig || await getAzureAvatarConfigFromEnv();

      // Get region for detailed logging
      const region = config.serviceRegion;

      debugLog("╔══════════════════════════════════════════════════════════════════════════════╗");
      debugLog("║  🌐 AZURE AVATAR - COMPLETE NETWORK CONNECTIVITY REQUIREMENTS               ║");
      debugLog("║  ══════════════════════════════════════════════════════════════════════════ ║");
      debugLog("║  FOR INFOSEC: All endpoints below MUST be accessible for avatar to work     ║");
      debugLog("╠══════════════════════════════════════════════════════════════════════════════╣");
      debugLog("║                                                                              ║");
      debugLog("║  📡 1. ICE TOKEN ENDPOINT (HTTPS REST API)                                  ║");
      debugLog("║     ─────────────────────────────────────────────────────────────────────── ║");
      debugLog(`║     FQDN: ${region}.tts.speech.microsoft.com`);
      debugLog("║     Port: 443 (TCP)");
      debugLog("║     Protocol: HTTPS");
      debugLog(`║     Full URL: https://${region}.tts.speech.microsoft.com/cognitiveservices/avatar/relay/token/v1`);
      debugLog("║                                                                              ║");
      debugLog("║  📡 2. SPEECH SYNTHESIS WEBSOCKET (Avatar TTS)                              ║");
      debugLog("║     ─────────────────────────────────────────────────────────────────────── ║");
      debugLog(`║     FQDN: ${region}.tts.speech.microsoft.com`);
      debugLog("║     Port: 443 (TCP)");
      debugLog("║     Protocol: WSS (WebSocket Secure)");
      debugLog(`║     Full URL: wss://${region}.tts.speech.microsoft.com/cognitiveservices/websocket/v1`);
      debugLog("║                                                                              ║");
      debugLog("║  📡 3. CUSTOM VOICE WEBSOCKET (if using custom avatar)                      ║");
      debugLog("║     ─────────────────────────────────────────────────────────────────────── ║");
      debugLog(`║     FQDN: ${region}.voice.speech.microsoft.com`);
      debugLog("║     Port: 443 (TCP)");
      debugLog("║     Protocol: WSS (WebSocket Secure)");
      debugLog(`║     Full URL: wss://${region}.voice.speech.microsoft.com/cognitiveservices/websocket/v1`);
      debugLog("║                                                                              ║");
      debugLog("║  📡 4. SPEECH RECOGNITION WEBSOCKET (STT for user voice)                    ║");
      debugLog("║     ─────────────────────────────────────────────────────────────────────── ║");
      debugLog(`║     FQDN: ${region}.stt.speech.microsoft.com`);
      debugLog("║     Port: 443 (TCP)");
      debugLog("║     Protocol: WSS (WebSocket Secure)");
      debugLog(`║     Full URL: wss://${region}.stt.speech.microsoft.com/speech/recognition/conversation/cognitiveservices/v1`);
      debugLog("║                                                                              ║");
      debugLog("║  📡 5. TURN/STUN RELAY SERVERS (WebRTC Media Transport)                     ║");
      debugLog("║     ─────────────────────────────────────────────────────────────────────── ║");
      debugLog("║     FQDN: relay.communication.microsoft.com");
      debugLog("║     Ports: 443 (TCP), 3478 (UDP + TCP)");
      debugLog("║     Protocol: TURN/STUN");
      debugLog("║     ⚠️  CRITICAL: Both UDP and TCP on port 3478 required!");
      debugLog("║                                                                              ║");
      debugLog("║  📡 6. AZURE COGNITIVE SERVICES (General API)                               ║");
      debugLog("║     ─────────────────────────────────────────────────────────────────────── ║");
      debugLog(`║     FQDN: ${region}.api.cognitive.microsoft.com`);
      debugLog("║     Port: 443 (TCP)");
      debugLog("║     Protocol: HTTPS");
      debugLog("║                                                                              ║");
      debugLog("╠══════════════════════════════════════════════════════════════════════════════╣");
      debugLog("║  📋 SUMMARY - FIREWALL RULES FOR INFOSEC:                                   ║");
      debugLog("║  ══════════════════════════════════════════════════════════════════════════ ║");
      debugLog(`║  ALLOW OUTBOUND TCP/443  → ${region}.tts.speech.microsoft.com`);
      debugLog(`║  ALLOW OUTBOUND TCP/443  → ${region}.voice.speech.microsoft.com`);
      debugLog(`║  ALLOW OUTBOUND TCP/443  → ${region}.stt.speech.microsoft.com`);
      debugLog(`║  ALLOW OUTBOUND TCP/443  → ${region}.api.cognitive.microsoft.com`);
      debugLog("║  ALLOW OUTBOUND TCP/443  → relay.communication.microsoft.com");
      debugLog("║  ALLOW OUTBOUND UDP/3478 → relay.communication.microsoft.com");
      debugLog("║  ALLOW OUTBOUND TCP/3478 → relay.communication.microsoft.com");
      debugLog("║                                                                              ║");
      debugLog("║  🔒 WILDCARD ALTERNATIVE (less restrictive):                                ║");
      debugLog("║  ALLOW OUTBOUND TCP/443  → *.speech.microsoft.com");
      debugLog("║  ALLOW OUTBOUND TCP/443  → *.cognitive.microsoft.com");
      debugLog("║  ALLOW OUTBOUND *        → relay.communication.microsoft.com");
      debugLog("╚══════════════════════════════════════════════════════════════════════════════╝");

      debugLog("╔══════════════════════════════════════════════════════════════════╗");
      debugLog("║  🔧 AVATAR CONFIGURATION                                         ║");
      debugLog("╠══════════════════════════════════════════════════════════════════╣");
      debugLog(`║  Region: ${config.serviceRegion}`);
      debugLog(`║  Speech Endpoint: https://${config.serviceRegion}.tts.speech.microsoft.com`);
      debugLog(`║  Voice: ${config.voiceName}`);
      debugLog(`║  Avatar Character: ${config.avatarCharacter}`);
      debugLog(`║  Custom Voice: ${config.customized ? 'Yes' : 'No'}`);
      if (config.deploymentId) {
        debugLog(`║  Deployment ID: ${config.deploymentId}`);
      }
      debugLog("╚══════════════════════════════════════════════════════════════════╝");
      
      // Use config values directly - no overrides
      const voiceName = config.voiceName;
      const avatarCharacter = config.avatarCharacter;
      const useBuiltInVoice = config.useBuiltInVoice ?? true;
      
      this.configuredVoiceName = voiceName;

      const speechSynthesisConfig = createSpeechConfig(config);

      // For custom voice, set the endpoint ID (deployment ID)
      if (config.deploymentId) {
        speechSynthesisConfig.endpointId = config.deploymentId;
        debugLog(`🎙️ Using custom voice deployment: ${config.deploymentId}`);
      }

      if (voiceName) {
        speechSynthesisConfig.speechSynthesisVoiceName = voiceName;
        debugLog(`🎙️ Setting voice name: ${voiceName}`);
      }
      
      debugLog(`🎙️ Speech config:`, {
        region: config.serviceRegion,
        voiceName: voiceName,
        deploymentId: config.deploymentId,
        customized: config.customized,
        useBuiltInVoice: useBuiltInVoice,
        avatarCharacter: avatarCharacter
      });
      
      // Log comprehensive config for Microsoft support
      debugLog("🔧 [AZURE_PERF] Avatar Configuration:", {
        subscriptionRegion: config.serviceRegion,
        voiceModel: voiceName,
        customVoiceDeployment: config.deploymentId,
        avatarCharacter: avatarCharacter,
        avatarStyle: config.avatarStyle,
        isCustomAvatar: config.customized,
        useBuiltInVoice: useBuiltInVoice,
        videoCrop: {
          x: AZURE_AVATAR_CONFIG.VIDEO_CROP_X,
          y: AZURE_AVATAR_CONFIG.VIDEO_CROP_Y,
          width: AZURE_AVATAR_CONFIG.VIDEO_CROP_WIDTH,
          height: AZURE_AVATAR_CONFIG.VIDEO_CROP_HEIGHT
        }
      });

      const videoFormat = new SpeechSDK.AvatarVideoFormat();
      videoFormat.setCropRange(
        new SpeechSDK.Coordinate(
          AZURE_AVATAR_CONFIG.VIDEO_CROP_X,
          AZURE_AVATAR_CONFIG.VIDEO_CROP_Y
        ),
        new SpeechSDK.Coordinate(
          AZURE_AVATAR_CONFIG.VIDEO_CROP_WIDTH,
          AZURE_AVATAR_CONFIG.VIDEO_CROP_HEIGHT
        )
      );

      const avatarConfig = new SpeechSDK.AvatarConfig(
        avatarCharacter,
        config.avatarStyle,
        videoFormat
      );
      
      // Set customized flag from backend configuration
      if (config.customized !== undefined) {
        avatarConfig.customized = config.customized;
      }
      
      // Set useBuiltInVoice from backend configuration
      avatarConfig.useBuiltInVoice = useBuiltInVoice;

      try {
        avatarConfig.backgroundImage = new URL(
          AZURE_AVATAR_CONFIG.BACKGROUND_IMAGE_URL
        );
      } catch (error) {
        avatarConfig.backgroundColor =
          AZURE_AVATAR_CONFIG.FALLBACK_BACKGROUND_COLOR;
      }

      const iceConfig = await this.fetchICEServerConfig(config);
      this.setupWebRTC(iceConfig);

      debugLog("╔══════════════════════════════════════════════════════════════════╗");
      debugLog("║  🎭 AVATAR DEBUG: Starting Avatar Session                        ║");
      debugLog("╚══════════════════════════════════════════════════════════════════╝");

      this.avatarSynthesizer = new SpeechSDK.AvatarSynthesizer(
        speechSynthesisConfig,
        avatarConfig
      );
      debugLog("✅ [AVATAR] AvatarSynthesizer created");

      debugLog("🚀 [AVATAR] Calling startAvatarAsync...");
      debugLog("📡 [WEBRTC] Peer connection state BEFORE startAvatarAsync:", {
        connectionState: this.peerConnection?.connectionState,
        iceConnectionState: this.peerConnection?.iceConnectionState,
        iceGatheringState: this.peerConnection?.iceGatheringState,
        signalingState: this.peerConnection?.signalingState
      });

      const startTime = Date.now();
      const result = await this.avatarSynthesizer.startAvatarAsync(
        this.peerConnection!
      );
      const duration = Date.now() - startTime;

      debugLog(`✅ [AVATAR] startAvatarAsync completed in ${duration}ms`);
      debugLog("📡 [WEBRTC] Peer connection state AFTER startAvatarAsync:", {
        connectionState: this.peerConnection?.connectionState,
        iceConnectionState: this.peerConnection?.iceConnectionState,
        iceGatheringState: this.peerConnection?.iceGatheringState,
        signalingState: this.peerConnection?.signalingState
      });
      debugLog("🎭 [AVATAR] Result:", {
        reason: result.reason,
        reasonName: SpeechSDK.ResultReason[result.reason],
        errorDetails: result.errorDetails || 'none'
      });

      if (result.reason === SpeechSDK.ResultReason.SynthesizingAudioCompleted) {
        this.isReady = true;
        this.showStatus(`
              <span style="display: block;line-height: 40px;letterSpacing: 8px">Your interview</span>
              <span style="display: block;line-height: 40px;letterSpacing: 8px">session is ready</span>
          `, AVATAR_STATUS_TYPES.READY);
        
        // Check if ICE is already connected (fast path)
        if (this.peerConnection?.iceConnectionState === "connected") {
          debugLog("✅ [AVATAR] ICE already connected, dispatching avatar_ready immediately");
          window.dispatchEvent(
            new CustomEvent(SOCKET_EVENTS.AVATAR_READY, {
              detail: { ready: true },
            })
          );
        } else {
          // ICE not connected yet - avatar_ready will be dispatched when ICE connects
          debugLog("⏳ [AVATAR] Waiting for ICE connection before dispatching avatar_ready...");
          debugLog("📡 [AVATAR] Current ICE state:", this.peerConnection?.iceConnectionState);
        }
      } else {
        if (result.reason === SpeechSDK.ResultReason.Canceled) {
          throw new Error(
            `MAI Interview start was canceled: ${
              result.errorDetails || "Unknown reason"
            }`
          );
        }
        throw new Error(
          `Failed to start MAI Interview session: ${result.errorDetails}`
        );
      }
    } catch (error) {
      this.showStatus(`Session failed: ${error}`, AVATAR_STATUS_TYPES.ERROR);
      throw error;
    }
  }

  /**
   * Fetch ICE server configuration via backend proxy
   * Backend fetches from Azure Speech Service using server-side credentials
   */
  private async fetchICEServerConfig(
    config: AzureAvatarConfig
  ): Promise<ICEServerConfig> {
    const backendUrl = process.env.REACT_APP_SERVER_URL || 'http://localhost:5294';
    const url = `${backendUrl}/api/avatarconfig/ice-token`;

    debugLog("╔══════════════════════════════════════════════════════════════════╗");
    debugLog("║  🌐 NETWORK DEBUG: ICE Token Fetch (via Backend)                 ║");
    debugLog("╠══════════════════════════════════════════════════════════════════╣");
    debugLog("║  Protocol: HTTPS (TCP/443)                                       ║");
    debugLog(`║  URL: ${url}`);
    debugLog("║  Note: Backend proxies to Azure Speech Service                   ║");
    debugLog("╚══════════════════════════════════════════════════════════════════╝");

    try {
      const response = await fetch(url);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        debugError("╔══════════════════════════════════════════════════════════════════╗");
        debugError("║  ❌ NETWORK ERROR: ICE Token Fetch Failed                        ║");
        debugError("╠══════════════════════════════════════════════════════════════════╣");
        debugError(`║  HTTP Status: ${response.status} ${response.statusText}`);
        debugError(`║  URL: ${url}`);
        debugError(`║  Error: ${errorData.error || 'Unknown error'}`);
        debugError("╚══════════════════════════════════════════════════════════════════╝");
        throw new Error(`HTTP ${response.status}: ${errorData.error || response.statusText}`);
      }

      const responseData: ICEServerConfig = await response.json();
      debugLog("✅ [NETWORK] ICE Token fetch SUCCESS (HTTP 200)");

      // Log credentials (masked for security)
      const maskedUsername = responseData.Username
        ? responseData.Username.substring(0, 20) + "..."
        : 'N/A';

      debugLog("╔══════════════════════════════════════════════════════════════════╗");
      debugLog("║  🔥 FIREWALL REQUIREMENTS: TURN/STUN Servers                     ║");
      debugLog("╠══════════════════════════════════════════════════════════════════╣");
      debugLog(`║  TURN Username: ${maskedUsername}`);
      debugLog(`║  TURN Credential: [REDACTED - ${responseData.Password?.length || 0} chars]`);
      debugLog("║  ─────────────────────────────────────────────────────────────────║");

      responseData.Urls.forEach((serverUrl: string, index: number) => {
        // Parse the URL to extract protocol, host, port, transport
        const match = serverUrl.match(/^(turn|stun):([^:?]+):?(\d+)?(\?transport=(\w+))?/);
        if (match) {
          const protocol = match[1].toUpperCase();
          const host = match[2];
          const port = match[3] || (protocol === 'STUN' ? '3478' : '443');
          const transport = match[5] || 'UDP+TCP';
          debugLog(`║  [${index + 1}] ${protocol} Server:`);
          debugLog(`║      FQDN: ${host}`);
          debugLog(`║      Port: ${port}`);
          debugLog(`║      Transport: ${transport.toUpperCase()}`);
          debugLog(`║      Full URL: ${serverUrl}`);
          debugLog("║  ─────────────────────────────────────────────────────────────────║");
        }
      });

      // Extract unique hosts and ports for firewall rules
      const firewallRules: Set<string> = new Set();
      responseData.Urls.forEach((serverUrl: string) => {
        const match = serverUrl.match(/^(turn|stun):([^:?]+):?(\d+)?/);
        if (match) {
          const host = match[2];
          const port = match[3] || '3478';
          firewallRules.add(`${host}:${port}`);
        }
      });

      debugLog("║                                                                  ║");
      debugLog("║  📋 FIREWALL RULES SUMMARY (for InfoSec):                        ║");
      debugLog("║  ─────────────────────────────────────────────────────────────────║");
      firewallRules.forEach(rule => {
        debugLog(`║  ALLOW OUTBOUND: ${rule} (UDP + TCP)`);
      });
      debugLog("║                                                                  ║");
      debugLog("║  📋 RAW SERVER URLs:                                             ║");
      responseData.Urls.forEach((serverUrl: string) => {
        debugLog(`║  → ${serverUrl}`);
      });
      debugLog("╚══════════════════════════════════════════════════════════════════╝");

      return responseData;
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        debugError("╔══════════════════════════════════════════════════════════════════╗");
        debugError("║  ❌ NETWORK ERROR: Cannot reach Backend Service                  ║");
        debugError("╠══════════════════════════════════════════════════════════════════╣");
        debugError(`║  URL: ${url}`);
        debugError("║  Protocol: HTTPS (TCP/443)                                       ║");
        debugError("║                                                                  ║");
        debugError("║  🔧 CHECK:                                                       ║");
        debugError("║  - Backend service is running                                    ║");
        debugError("║  - REACT_APP_SERVER_URL is correctly configured                  ║");
        debugError("╚══════════════════════════════════════════════════════════════════╝");
      }
      throw error;
    }
  }

  /**
   * Setup WebRTC connection
   * @param iceConfig - ICE server configuration from Azure
   * @param forceTcpOnly - If true, filter ICE servers to TCP-only (fallback mode)
   */
  private setupWebRTC(iceConfig: ICEServerConfig, forceTcpOnly: boolean = false): void {
    // Store config for potential TCP fallback retry
    this.currentIceConfig = iceConfig;
    this.iceConnectionAttemptStart = Date.now();

    // Filter/modify URLs for TCP-only mode if requested
    let iceUrls = iceConfig.Urls;
    if (forceTcpOnly) {
      debugLog("╔══════════════════════════════════════════════════════════════════╗");
      debugLog("║  🔄 TCP FALLBACK MODE ACTIVATED                                  ║");
      debugLog("║  Filtering ICE servers to TCP-only transport                     ║");
      debugLog("╚══════════════════════════════════════════════════════════════════╝");

      // Filter to only TCP URLs or convert UDP to TCP
      iceUrls = iceConfig.Urls
        .filter(url => url.includes('transport=tcp') || url.includes(':443'))
        .map(url => {
          // If URL doesn't specify transport, add TCP
          if (!url.includes('transport=')) {
            return url + (url.includes('?') ? '&' : '?') + 'transport=tcp';
          }
          return url;
        });

      // If no TCP URLs found, create TCP versions of existing URLs
      if (iceUrls.length === 0) {
        iceUrls = iceConfig.Urls.map(url => {
          const baseUrl = url.split('?')[0];
          return baseUrl + '?transport=tcp';
        });
      }

      debugLog("🔧 [TCP FALLBACK] Modified ICE URLs:");
      iceUrls.forEach((url, i) => debugLog(`   [${i + 1}] ${url}`));
    }

    // Analyze transport types in URLs
    const udpUrls = iceUrls.filter(url => !url.includes('transport=tcp'));
    const tcpUrls = iceUrls.filter(url => url.includes('transport=tcp'));

    debugLog("╔══════════════════════════════════════════════════════════════════╗");
    debugLog("║  🔧 WEBRTC DEBUG: Setting up RTCPeerConnection                   ║");
    debugLog("╠══════════════════════════════════════════════════════════════════╣");
    debugLog(`║  ICE Transport Policy: ${WEBRTC_CONFIG.ICE_TRANSPORT_POLICY}`);
    debugLog(`║  TCP Fallback Mode: ${forceTcpOnly ? 'YES' : 'NO'}`);
    debugLog(`║  ICE Servers configured: ${iceUrls.length}`);
    debugLog(`║    - UDP transport: ${udpUrls.length} URLs`);
    debugLog(`║    - TCP transport: ${tcpUrls.length} URLs`);
    debugLog("║  ─────────────────────────────────────────────────────────────────║");
    iceUrls.forEach((url, i) => {
      const transport = url.includes('transport=tcp') ? 'TCP' : 'UDP';
      const port = url.match(/:(\d+)/)?.[1] || 'default';
      debugLog(`║    [${i + 1}] ${transport} port:${port} → ${url.substring(0, 50)}...`);
    });
    debugLog("╚══════════════════════════════════════════════════════════════════╝");

    this.peerConnection = new RTCPeerConnection({
      iceServers: [
        {
          urls: iceUrls,
          username: iceConfig.Username,
          credential: iceConfig.Password,
        },
      ],
      iceTransportPolicy: WEBRTC_CONFIG.ICE_TRANSPORT_POLICY as RTCIceTransportPolicy,
    });

    debugLog("✅ [WEBRTC] RTCPeerConnection created successfully");

    // Track ICE candidates for debugging with transport info
    const iceCandidates: Array<{type: string; protocol: string; address: string; port: number; relayProtocol?: string}> = [];

    this.peerConnection.onicecandidate = (event) => {
      if (event.candidate) {
        // Extract relay protocol from candidate (for TURN candidates)
        const relayProtocol = (event.candidate as any).relayProtocol ||
          (event.candidate.candidate?.match(/relay-protocol\s+(\w+)/)?.[1]) ||
          undefined;

        const candidate = {
          type: event.candidate.type || 'unknown',
          protocol: event.candidate.protocol || 'unknown',
          address: event.candidate.address || 'unknown',
          port: event.candidate.port || 0,
          relayProtocol: relayProtocol
        };
        iceCandidates.push(candidate);

        // Enhanced logging with transport details
        const transportInfo = candidate.relayProtocol
          ? `${candidate.protocol.toUpperCase()} (relay: ${candidate.relayProtocol.toUpperCase()})`
          : candidate.protocol.toUpperCase();
        debugLog(`🧊 [WEBRTC] ICE Candidate: ${candidate.type.toUpperCase()} via ${transportInfo} → ${candidate.address}:${candidate.port}`);
      } else {
        // ICE gathering complete - detailed transport analysis
        const relays = iceCandidates.filter(c => c.type === 'relay');
        const hosts = iceCandidates.filter(c => c.type === 'host');
        const srflx = iceCandidates.filter(c => c.type === 'srflx');

        // Break down relay candidates by transport
        const relaysUdp = relays.filter(c => c.protocol === 'udp');
        const relaysTcp = relays.filter(c => c.protocol === 'tcp');
        const relaysByRelayProtocol = {
          udp: relays.filter(c => c.relayProtocol === 'udp'),
          tcp: relays.filter(c => c.relayProtocol === 'tcp'),
          tls: relays.filter(c => c.relayProtocol === 'tls'),
        };

        debugLog("╔══════════════════════════════════════════════════════════════════╗");
        debugLog("║  🧊 ICE CANDIDATE GATHERING COMPLETE                             ║");
        debugLog("╠══════════════════════════════════════════════════════════════════╣");
        debugLog(`║  Total candidates found: ${iceCandidates.length}`);
        debugLog("║  ─────────────────────────────────────────────────────────────────║");
        debugLog(`║  📍 Host candidates: ${hosts.length}`);
        debugLog(`║  📍 Server Reflexive (STUN): ${srflx.length}`);
        debugLog(`║  📍 Relay (TURN): ${relays.length}`);
        debugLog("║  ─────────────────────────────────────────────────────────────────║");
        debugLog("║  🔌 TRANSPORT BREAKDOWN (Critical for VPN users):                ║");
        debugLog(`║     Relay via UDP: ${relaysUdp.length} candidates`);
        debugLog(`║     Relay via TCP: ${relaysTcp.length} candidates`);
        if (relaysByRelayProtocol.udp.length > 0 || relaysByRelayProtocol.tcp.length > 0) {
          debugLog("║  ─────────────────────────────────────────────────────────────────║");
          debugLog("║  🔄 TURN RELAY PROTOCOL (server-side transport):                 ║");
          debugLog(`║     TURN over UDP: ${relaysByRelayProtocol.udp.length}`);
          debugLog(`║     TURN over TCP: ${relaysByRelayProtocol.tcp.length}`);
          debugLog(`║     TURN over TLS: ${relaysByRelayProtocol.tls.length}`);
        }

        if (relays.length === 0) {
          debugWarn("║  ─────────────────────────────────────────────────────────────────║");
          debugWarn("║  ⚠️  NO RELAY CANDIDATES FOUND!                                  ║");
          debugWarn("║  TURN servers are likely blocked by firewall/VPN                 ║");
          debugWarn("║  ─────────────────────────────────────────────────────────────────║");
          debugWarn("║  🔧 TROUBLESHOOTING:                                             ║");
          debugWarn("║  1. Check if UDP port 3478 is allowed through VPN                ║");
          debugWarn("║  2. Check if TCP port 443/3478 is allowed                        ║");
          debugWarn("║  3. VPN may not tunnel UDP - try TCP fallback                    ║");
        } else if (relaysTcp.length === 0 && relaysUdp.length > 0) {
          debugLog("║  ─────────────────────────────────────────────────────────────────║");
          debugLog("║  ℹ️  Only UDP relay candidates found                              ║");
          debugLog("║  If connection fails, TCP fallback may be needed                  ║");
        }
        debugLog("╚══════════════════════════════════════════════════════════════════╝");
      }
    };

    // ICE gathering state changes
    this.peerConnection.onicegatheringstatechange = () => {
      debugLog(`🧊 [WEBRTC] ICE Gathering State: ${this.peerConnection?.iceGatheringState}`);
    };

    // Connection state changes with TCP fallback logic
    this.peerConnection.onconnectionstatechange = () => {
      const state = this.peerConnection?.connectionState;
      const elapsed = Date.now() - this.iceConnectionAttemptStart;
      debugLog(`📡 [WEBRTC] Connection State: ${state} (elapsed: ${elapsed}ms)`);

      if (state === 'failed') {
        debugError("╔══════════════════════════════════════════════════════════════════╗");
        debugError("║  ❌ WEBRTC CONNECTION FAILED                                     ║");
        debugError("╠══════════════════════════════════════════════════════════════════╣");
        debugError(`║  Connection attempt duration: ${elapsed}ms`);
        debugError(`║  TCP Fallback attempted: ${this.tcpFallbackAttempted ? 'YES' : 'NO'}`);
        debugError("║  ─────────────────────────────────────────────────────────────────║");

        // Attempt TCP fallback if not already tried
        if (!this.tcpFallbackAttempted && this.currentIceConfig) {
          debugWarn("║  🔄 ATTEMPTING TCP FALLBACK...                                   ║");
          debugWarn("║  UDP may be blocked by VPN/firewall - trying TCP only            ║");
          debugError("╚══════════════════════════════════════════════════════════════════╝");

          this.tcpFallbackAttempted = true;

          // Dispatch event to trigger reconnection with TCP
          window.dispatchEvent(new CustomEvent('avatar-ice-failed-try-tcp', {
            detail: { iceConfig: this.currentIceConfig }
          }));
        } else {
          debugError("║  Likely cause: Firewall blocking TURN/STUN traffic               ║");
          debugError("║                                                                  ║");
          debugError("║  🔧 FIREWALL ACTION REQUIRED:                                    ║");
          debugError("║  Allow outbound to: relay.communication.microsoft.com           ║");
          debugError("║  Ports: 443 (TCP), 3478 (UDP+TCP)                               ║");
          debugError("║                                                                  ║");
          if (this.tcpFallbackAttempted) {
            debugError("║  ⚠️  TCP FALLBACK ALSO FAILED                                   ║");
            debugError("║  Both UDP and TCP connections are blocked                       ║");
            debugError("║  VPN may not be routing traffic to Azure endpoints              ║");
          }
          debugError("╚══════════════════════════════════════════════════════════════════╝");
        }
      } else if (state === 'connected') {
        const transport = this.tcpFallbackAttempted ? 'TCP (fallback)' : 'Default (UDP preferred)';
        debugLog("╔══════════════════════════════════════════════════════════════════╗");
        debugLog("║  ✅ WEBRTC CONNECTION ESTABLISHED                                ║");
        debugLog("╠══════════════════════════════════════════════════════════════════╣");
        debugLog(`║  Connection time: ${elapsed}ms`);
        debugLog(`║  Transport mode: ${transport}`);
        debugLog("╚══════════════════════════════════════════════════════════════════╝");

        // Reset fallback state on successful connection
        this.tcpFallbackAttempted = false;
      }
    };

    // Signaling state changes
    this.peerConnection.onsignalingstatechange = () => {
      debugLog(`📡 [WEBRTC] Signaling State: ${this.peerConnection?.signalingState}`);
    };

    this.peerConnection.ontrack = (event) => {
      debugLog(`📹 [WEBRTC] Media Track Received: ${event.track.kind}`);
      debugLog(`    Track ID: ${event.track.id}`);
      debugLog(`    Track State: ${event.track.readyState}`);

      if (!this.avatarElement) {
        debugError("❌ [WEBRTC] Avatar element not found!");
        return;
      }

      if (event.track.kind === "video") {
        debugLog("📹 [WEBRTC] Setting up VIDEO track...");
        let videoElement = document.getElementById(
          AVATAR_ELEMENT_IDS.AVATAR_VIDEO
        ) as HTMLVideoElement;

        if (!videoElement) {
          videoElement = document.createElement("video") as HTMLVideoElement;
          videoElement.id = AVATAR_ELEMENT_IDS.AZURE_AVATAR_VIDEO;
          videoElement.className =
            "avatar-video object-cover w-full h-full object-center bg-black max-w-full max-h-full";
          this.avatarElement.appendChild(videoElement);
        }

        videoElement.srcObject = event.streams[0];
        debugLog(`📹 [WEBRTC] Video srcObject assigned, stream active: ${event.streams[0]?.active}`);
        configureVideoElement(videoElement);

        videoElement.addEventListener("loadeddata", () => {
          debugLog("📹 [WEBRTC] Video data loaded, attempting playback...");
          videoElement.play().catch((err) => {
            debugError("❌ [WEBRTC] Video play failed:", err);
          });
        });

        videoElement.addEventListener("play", () => {
          debugLog("✅ [WEBRTC] Video playback STARTED");
          this.showStatus("", AVATAR_STATUS_TYPES.READY);
        });

        videoElement.addEventListener("stalled", () => {
          debugWarn("⚠️ [WEBRTC] Video stalled - possible network issue");
        });

        videoElement.addEventListener("error", (e) => {
          debugError("❌ [WEBRTC] Video error:", e);
        });
      } else if (event.track.kind === "audio") {
        debugLog("🔊 [WEBRTC] Setting up AUDIO track...");
        const audioElement = document.createElement(
          "audio"
        ) as HTMLAudioElement;
        audioElement.srcObject = event.streams[0];
        audioElement.autoplay = true;
        audioElement.muted = false;
        audioElement.style.display = "none";
        this.avatarElement.appendChild(audioElement);
        debugLog("✅ [WEBRTC] Audio element created and attached");
      }
    };

    // Listen to WebRTC data channel events from avatar service
    this.peerConnection.addEventListener("datachannel", (event) => {
      debugLog("📡 [AvatarService] WebRTC data channel established");
      const dataChannel = event.channel;
      
      dataChannel.onmessage = (e) => {
        try {
          const webRTCEvent = JSON.parse(e.data);
          const eventType = webRTCEvent.event?.eventType;
          
          debugLog(`🎤 [AvatarService] WebRTC Event: ${eventType}`);
          
          // EVENT_TYPE_TURN_END signals that avatar has finished speaking the current turn
          // This happens BEFORE SWITCH_TO_IDLE and is the earliest reliable signal
          if (eventType === 'EVENT_TYPE_TURN_END') {
            debugLog("✅ [AvatarService] Avatar turn ended - speech audio completed");
            // Dispatch event for microphone unmuting - this is the fastest signal!
            window.dispatchEvent(new CustomEvent('avatar-speech-ended'));
          }
          else if (eventType === 'EVENT_TYPE_SWITCH_TO_IDLE') {
            debugLog("✅ [AvatarService] Avatar went idle - speech completed");
            // Dispatch event for session handlers (queue processing, etc.)
            window.dispatchEvent(new CustomEvent('avatar-idle'));
            // Dispatch separate event for internal queue processing
            window.dispatchEvent(new CustomEvent('avatar-idle-internal'));
          }
          else if (eventType === 'EVENT_TYPE_TURN_START') {
            debugLog("🔊 [AvatarService] Avatar started speaking");
            window.dispatchEvent(new CustomEvent('avatar-speaking-start'));
          }
          else if (eventType === 'EVENT_TYPE_SESSION_END') {
            debugLog("⚠️ [AvatarService] Avatar session ended unexpectedly");
            window.dispatchEvent(new CustomEvent('avatar-session-ended'));
          }
        } catch (error) {
          debugError("❌ [AvatarService] Error parsing WebRTC event:", error);
        }
      };
      
      dataChannel.onerror = (error) => {
        debugError("❌ [AvatarService] WebRTC data channel error:", error);
      };
    });

    // Create a data channel from client side to enable server events
    this.peerConnection.createDataChannel("eventChannel");

    this.peerConnection.oniceconnectionstatechange = () => {
      const state = this.peerConnection?.iceConnectionState;

      // Log all state transitions with timestamp
      const timestamp = new Date().toISOString();
      debugLog(`🧊 [WEBRTC] ICE Connection State: ${state} (${timestamp})`);

      if (state === "connected") {
        debugLog("╔══════════════════════════════════════════════════════════════════╗");
        debugLog("║  ✅ ICE CONNECTION ESTABLISHED                                   ║");
        debugLog("║  Media streams should now flow successfully                      ║");
        debugLog("╚══════════════════════════════════════════════════════════════════╝");

        // Log connection stats
        this.logConnectionStats();

        this.isConnected = true;
        if (this.reconnectionTimeout) {
          clearTimeout(this.reconnectionTimeout);
          this.reconnectionTimeout = null;
        }

        // Start health monitoring when connected
        this.startHealthMonitoring();
        
        // NOW dispatch avatar_ready - WebRTC is truly ready for speech
        if (this.isReady) {
          debugLog("✅ [AVATAR] ICE connected + Avatar ready = Dispatching avatar_ready NOW");
          window.dispatchEvent(
            new CustomEvent(SOCKET_EVENTS.AVATAR_READY, {
              detail: { ready: true },
            })
          );
        } else {
          debugLog("⏳ [AVATAR] ICE connected but avatar not ready yet - will dispatch when ready");
        }
      } else if (state === "checking") {
        debugLog("🔍 [WEBRTC] ICE checking connectivity to TURN/STUN servers...");
      } else if (state === "disconnected") {
        debugWarn("╔══════════════════════════════════════════════════════════════════╗");
        debugWarn("║  ⚠️  ICE CONNECTION DISCONNECTED                                 ║");
        debugWarn("║  Attempting to recover...                                        ║");
        debugWarn("╚══════════════════════════════════════════════════════════════════╝");
        this.isConnected = false;

        if (!this.reconnectionTimeout && !this.isRecovering) {
          this.reconnectionTimeout = setTimeout(async () => {
            try {
              this.isRecovering = true;
              await this.reinitializeAvatar();
            } catch (error) {
              this.showStatus(
                "Connection lost - please refresh",
                AVATAR_STATUS_TYPES.ERROR
              );
            } finally {
              this.isRecovering = false;
              this.reconnectionTimeout = null;
            }
          }, AZURE_AVATAR_CONFIG.RECOVERY_DELAY_MS);
        }
      } else if (state === "failed") {
        debugError("╔══════════════════════════════════════════════════════════════════╗");
        debugError("║  ❌ ICE CONNECTION FAILED                                        ║");
        debugError("╠══════════════════════════════════════════════════════════════════╣");
        debugError("║  The WebRTC connection could not be established.                 ║");
        debugError("║                                                                  ║");
        debugError("║  🔧 FIREWALL REQUIREMENTS FOR INFOSEC:                           ║");
        debugError("║  ──────────────────────────────────────────────────────────────  ║");
        debugError("║  1. TURN Server (Microsoft Azure Communication Services):       ║");
        debugError("║     Host: relay.communication.microsoft.com                     ║");
        debugError("║     Ports: 443 (TCP), 3478 (UDP + TCP)                          ║");
        debugError("║                                                                  ║");
        debugError("║  2. Azure Speech Service:                                       ║");
        debugError("║     Host: *.tts.speech.microsoft.com                            ║");
        debugError("║     Port: 443 (TCP/HTTPS)                                       ║");
        debugError("║                                                                  ║");
        debugError("║  3. Azure Cognitive Services WebSocket:                         ║");
        debugError("║     Host: *.stt.speech.microsoft.com                            ║");
        debugError("║     Port: 443 (TCP/WSS)                                         ║");
        debugError("╚══════════════════════════════════════════════════════════════════╝");

        this.isConnected = false;

        if (!this.isRecovering) {
          setTimeout(async () => {
            try {
              this.isRecovering = true;
              await this.reinitializeAvatar();
            } catch (error) {
              this.showStatus(
                "Connection failed - please refresh",
                AVATAR_STATUS_TYPES.ERROR
              );
            } finally {
              this.isRecovering = false;
            }
          }, AZURE_AVATAR_CONFIG.FAILED_CONNECTION_RECOVERY_DELAY_MS);
        }
      } else if (state === "closed") {
        debugLog("🔒 [WEBRTC] ICE Connection closed");
        this.isConnected = false;
      }
    };

    this.peerConnection.addTransceiver("video", {
      direction: WEBRTC_CONFIG.TRANSCEIVER_DIRECTION as any,
    });
    this.peerConnection.addTransceiver("audio", {
      direction: WEBRTC_CONFIG.TRANSCEIVER_DIRECTION as any,
    });
  }

  /**
   * Retry WebRTC connection with TCP-only transport (fallback for VPN/firewall issues)
   * This is triggered when UDP connections fail, which is common with corporate VPNs
   */
  private async retryWithTcpFallback(iceConfig: ICEServerConfig): Promise<void> {
    debugLog("╔══════════════════════════════════════════════════════════════════╗");
    debugLog("║  🔄 TCP FALLBACK: Reinitializing WebRTC with TCP-only            ║");
    debugLog("╠══════════════════════════════════════════════════════════════════╣");
    debugLog("║  Reason: Initial connection failed (likely UDP blocked by VPN)   ║");
    debugLog("║  Action: Forcing TURN over TCP transport                         ║");
    debugLog("╚══════════════════════════════════════════════════════════════════╝");

    try {
      // Close existing peer connection
      if (this.peerConnection) {
        this.peerConnection.close();
        this.peerConnection = null;
      }

      // Close avatar synthesizer
      if (this.avatarSynthesizer) {
        this.avatarSynthesizer.close();
        this.avatarSynthesizer = null;
      }

      this.isReady = false;
      this.isConnected = false;

      // Small delay before retry
      await new Promise(resolve => setTimeout(resolve, 500));

      // Re-setup WebRTC with TCP-only mode
      this.setupWebRTC(iceConfig, true); // forceTcpOnly = true

      // Get config and reinitialize avatar synthesizer
      const config = this.externalConfig || await getAzureAvatarConfigFromEnv();

      const speechConfig = createSpeechConfig(config);

      const avatarConfig = new SpeechSDK.AvatarConfig(
        config.avatarCharacter,
        config.avatarStyle || "",
        new SpeechSDK.AvatarVideoFormat()
      );
      avatarConfig.customized = config.customized ?? false;

      if (config.useBuiltInVoice) {
        avatarConfig.useBuiltInVoice = true;
      }

      this.avatarSynthesizer = new SpeechSDK.AvatarSynthesizer(
        speechConfig,
        avatarConfig
      );

      // Reconnect to peer connection
      await this.avatarSynthesizer.startAvatarAsync(this.peerConnection!);

      debugLog("✅ [TCP FALLBACK] Avatar reinitialized with TCP transport");
      this.isReady = true;

    } catch (error) {
      debugError("❌ [TCP FALLBACK] Failed to reinitialize with TCP:", error);
      this.showStatus("Connection failed (TCP fallback) - please refresh", AVATAR_STATUS_TYPES.ERROR);
      throw error;
    }
  }

  /**
   * Make avatar speak text with optional emotion (queue-based)
   */
  async speak(text: string, emotion?: string): Promise<void> {
    debugLog("🎤 [AvatarService] speak() called:", {
      text: text.substring(0, 50),
      emotion,
      isReady: this.isReady,
      hasSynthesizer: !!this.avatarSynthesizer,
      currentlySpeaking: this.currentlySpeaking,
      queueLength: this.speechQueue.length
    });

    if (!this.isReady || !this.avatarSynthesizer) {
      debugWarn("⚠️ [AvatarService] speak() Cannot proceed - avatar not ready or no synthesizer", {
        isReady: this.isReady,
        hasSynthesizer: !!this.avatarSynthesizer,
        isConnected: this.isConnected
      });
      return;
    }

    // If already speaking, queue this message
    if (this.currentlySpeaking) {
      debugLog("📋 [AvatarService] Already speaking, queueing message");
      this.speechQueue.push({ text, emotion });
      return;
    }

    // Start speaking
    await this.speakNext(text, emotion);
  }

  /**
   * Process next speech item from queue
   */
  private async speakNext(text: string, emotion?: string): Promise<void> {
    this.currentlySpeaking = true;

    try {
      // Only unmute audio elements once (optimization: saves 1-2ms per sentence)
      if (!this.isAudioUnmuted) {
        debugLog("🔊 [AvatarService] Unmuting audio elements (first time)...");
        unmuteAllAudioElements();

        const avatarVideo = document.getElementById(
          AVATAR_ELEMENT_IDS.AVATAR_VIDEO
        ) as HTMLVideoElement;
        if (avatarVideo) {
          avatarVideo.muted = false;
          avatarVideo.volume = AZURE_AVATAR_CONFIG.AUDIO_VOLUME;
          debugLog("📹 [AvatarService] Video element unmuted");
        }
        
        this.isAudioUnmuted = true;
      }

      const ssml = generateSSML(text, this.configuredVoiceName, emotion, this.configuredVoiceSpeed);
      debugLog("📝 [AvatarService] Calling speakSsmlAsync...");
      debugLog("📊 [AZURE_PERF] Frontend synthesis request:", {
        textLength: text.length,
        voiceName: this.configuredVoiceName,
        ssml: ssml
      });
      
      const startTime = Date.now();
      const result = await this.avatarSynthesizer!.speakSsmlAsync(ssml);
      const synthesisDuration = Date.now() - startTime;
      const charsPerSecond = text.length / (synthesisDuration / 1000);
      
      debugLog("✅ [AvatarService] speakSsmlAsync completed:", { 
        synthesisDurationMs: synthesisDuration,
        resultReason: result?.reason
      });
      
      // Latency profiling (Microsoft pattern)
      const expectedDuration = text.length * 50; // Expected: ~50ms per character for good performance
      const performanceRatio = synthesisDuration / expectedDuration;
      
      debugLog("⏱️ [AZURE_PERF] Frontend TTS synthesis latency:", {
        textLength: text.length,
        durationMs: synthesisDuration,
        charsPerSecond: charsPerSecond.toFixed(2),
        expectedMs: expectedDuration,
        performanceRatio: performanceRatio.toFixed(2) + "x",
        status: performanceRatio > 2 ? "SLOW" : performanceRatio > 1.5 ? "ACCEPTABLE" : "GOOD"
      });
      
      debugLog("🎯 [LATENCY_PROFILE] TTS Avatar timing:", {
        text: text.substring(0, 100) + (text.length > 100 ? "..." : ""),
        metrics: {
          "Text length": text.length + " chars",
          "TTS latency": synthesisDuration + "ms", 
          "Synthesis rate": charsPerSecond.toFixed(2) + " chars/sec",
          "Performance": performanceRatio > 2 ? "⚠️ NEEDS OPTIMIZATION" : "✅ ACCEPTABLE"
        }
      });

      // NOTE: We DON'T set currentlySpeaking = false here
      // Instead, we wait for the WebRTC 'EVENT_TYPE_SWITCH_TO_IDLE' event
      // which indicates the avatar truly finished speaking and went idle
      
    } catch (error) {
      debugError("❌ [AvatarService] Error in speakNext():", error);
      this.currentlySpeaking = false;
      
      // Process queue even on error
      this.processNextInQueue();
      throw error;
    }
  }

  /**
   * Process the next item in the speech queue
   */
  private processNextInQueue(): void {
    if (this.speechQueue.length > 0) {
      debugLog(`📋 [AvatarService] Processing next queued item (${this.speechQueue.length} remaining)`);
      const next = this.speechQueue.shift()!;
      this.speakNext(next.text, next.emotion);
    } else {
      debugLog("✅ [AvatarService] Queue empty, setting currentlySpeaking = false");
      this.currentlySpeaking = false;
    }
  }

  /**
   * Make avatar speak SSML directly (from backend)
   */
  async speakSSML(ssml: string): Promise<void> {
    debugLog("🎤 [AvatarService] speakSSML() called with SSML from backend:", {
      ssmlLength: ssml.length,
      isReady: this.isReady,
      hasSynthesizer: !!this.avatarSynthesizer,
      isConnected: this.isConnected
    });

    if (!this.isReady || !this.avatarSynthesizer) {
      debugWarn("⚠️ [AvatarService] speakSSML() Cannot proceed - avatar not ready or no synthesizer", {
        isReady: this.isReady,
        hasSynthesizer: !!this.avatarSynthesizer,
        isConnected: this.isConnected
      });
      return;
    }

    try {
      // Only unmute audio elements once (optimization: saves 1-2ms per sentence)
      if (!this.isAudioUnmuted) {
        debugLog("🔊 [AvatarService] Unmuting audio elements (first time)...");
        unmuteAllAudioElements();

        const avatarVideo = document.getElementById(
          AVATAR_ELEMENT_IDS.AVATAR_VIDEO
        ) as HTMLVideoElement;
        if (avatarVideo) {
          avatarVideo.muted = false;
          avatarVideo.volume = AZURE_AVATAR_CONFIG.AUDIO_VOLUME;
        }
        
        this.isAudioUnmuted = true;
      }
      
      const avatarVideo = document.getElementById(
        AVATAR_ELEMENT_IDS.AVATAR_VIDEO
      ) as HTMLVideoElement;
      if (avatarVideo) {
        debugLog("📹 [AvatarService] Video element:", {
          muted: avatarVideo.muted,
          volume: avatarVideo.volume,
          paused: avatarVideo.paused,
          readyState: avatarVideo.readyState
        });
      }

      debugLog("📝 [AvatarService] Calling speakSsmlAsync with SSML...");
      debugLog("📡 [WEBRTC] Connection state before speech:", {
        connectionState: this.peerConnection?.connectionState,
        iceConnectionState: this.peerConnection?.iceConnectionState
      });

      const startTime = Date.now();

      const result = await this.avatarSynthesizer.speakSsmlAsync(ssml);
      const duration = Date.now() - startTime;

      debugLog("✅ [AvatarService] speakSsmlAsync completed:", {
        durationMs: duration,
        resultReason: result?.reason,
        reasonName: result?.reason !== undefined ? SpeechSDK.ResultReason[result.reason] : 'undefined',
        errorDetails: result?.errorDetails || 'none'
      });

      // Check for issues
      if (result?.reason === SpeechSDK.ResultReason.Canceled) {
        debugError("❌ [AVATAR] Speech synthesis CANCELED - check network/firewall");
        debugError("    Error details:", result.errorDetails);
      }

      debugLog("📡 [WEBRTC] Connection state after speech:", {
        connectionState: this.peerConnection?.connectionState,
        iceConnectionState: this.peerConnection?.iceConnectionState
      });
    } catch (error) {
      debugError("❌ [AvatarService] Error in speakSSML():", error);
      throw error;
    }
  }

  /**
   * Play backend-generated audio with avatar lip-sync
   * Tests if AvatarSynthesizer can sync lips to pre-generated audio
   */
  async speakWithAudio(audioDataUrl: string): Promise<void> {
    debugLog("🎤 [AvatarService] speakWithAudio() called with backend audio:", {
      audioUrlLength: audioDataUrl?.length,
      isReady: this.isReady,
      hasSynthesizer: !!this.avatarSynthesizer,
      isConnected: this.isConnected
    });

    if (!this.isReady || !this.avatarSynthesizer) {
      debugWarn("⚠️ [AvatarService] speakWithAudio() Cannot proceed - avatar not ready or no synthesizer", {
        isReady: this.isReady,
        hasSynthesizer: !!this.avatarSynthesizer,
        isConnected: this.isConnected
      });
      return;
    }

    // Test approach based on official samples - but likely won't work for lip-sync
    debugLog("🧪 [AvatarService] Testing backend audio with avatar...");
    
    try {
      // Create audio element
      const audio = document.createElement('audio');
      audio.src = audioDataUrl;
      audio.autoplay = true;
      
      debugLog("✅ [AvatarService] Playing backend audio (lip-sync may not work)");
    } catch (error) {
      debugError("❌ [AvatarService] Error in speakWithAudio():", error);
      throw error;
    }
  }

  /**
   * Log WebRTC connection stats for debugging - shows active transport (UDP vs TCP)
   */
  private logConnectionStats(): void {
    if (!this.peerConnection) return;

    this.peerConnection.getStats().then(stats => {
      debugLog("╔══════════════════════════════════════════════════════════════════╗");
      debugLog("║  📊 WEBRTC CONNECTION STATS - ACTIVE TRANSPORT                   ║");
      debugLog("╠══════════════════════════════════════════════════════════════════╣");

      // Collect all candidates for lookup
      const candidates: Map<string, any> = new Map();
      stats.forEach(report => {
        if (report.type === 'local-candidate' || report.type === 'remote-candidate') {
          candidates.set(report.id, report);
        }
      });

      // Find the active candidate pair
      stats.forEach(report => {
        if (report.type === 'candidate-pair' && report.state === 'succeeded') {
          const localCandidate = candidates.get(report.localCandidateId);
          const remoteCandidate = candidates.get(report.remoteCandidateId);

          debugLog("║  ✅ ACTIVE CONNECTION:                                           ║");
          debugLog("║  ─────────────────────────────────────────────────────────────────║");

          if (localCandidate) {
            const relayProto = localCandidate.relayProtocol ? ` (relay: ${localCandidate.relayProtocol.toUpperCase()})` : '';
            debugLog(`║  📤 LOCAL: ${localCandidate.candidateType?.toUpperCase() || 'unknown'}`);
            debugLog(`║     Protocol: ${localCandidate.protocol?.toUpperCase() || 'unknown'}${relayProto}`);
            debugLog(`║     Address: ${localCandidate.address}:${localCandidate.port}`);
            if (localCandidate.candidateType === 'relay') {
              debugLog(`║     TURN Server: ${localCandidate.relatedAddress || 'N/A'}:${localCandidate.relatedPort || 'N/A'}`);
            }
          }

          if (remoteCandidate) {
            const relayProto = remoteCandidate.relayProtocol ? ` (relay: ${remoteCandidate.relayProtocol.toUpperCase()})` : '';
            debugLog("║  ─────────────────────────────────────────────────────────────────║");
            debugLog(`║  📥 REMOTE: ${remoteCandidate.candidateType?.toUpperCase() || 'unknown'}`);
            debugLog(`║     Protocol: ${remoteCandidate.protocol?.toUpperCase() || 'unknown'}${relayProto}`);
            debugLog(`║     Address: ${remoteCandidate.address}:${remoteCandidate.port}`);
          }

          debugLog("║  ─────────────────────────────────────────────────────────────────║");
          debugLog(`║  📈 Bytes Sent: ${report.bytesSent || 0}`);
          debugLog(`║  📉 Bytes Received: ${report.bytesReceived || 0}`);
          debugLog(`║  ⏱️  Current RTT: ${report.currentRoundTripTime ? (report.currentRoundTripTime * 1000).toFixed(2) + 'ms' : 'N/A'}`);

          // Highlight transport type for VPN troubleshooting
          const transport = localCandidate?.protocol?.toUpperCase() || 'UNKNOWN';
          const isRelay = localCandidate?.candidateType === 'relay';
          debugLog("║  ─────────────────────────────────────────────────────────────────║");
          debugLog(`║  🔌 TRANSPORT SUMMARY: ${transport} ${isRelay ? '(via TURN relay)' : '(direct)'}`);

          if (transport === 'UDP' && isRelay) {
            debugLog("║  ℹ️  Using UDP TURN relay - if issues occur, TCP fallback available");
          } else if (transport === 'TCP' && isRelay) {
            debugLog("║  ✅ Using TCP TURN relay - best for VPN/restricted networks");
          }
        }
      });

      debugLog("╚══════════════════════════════════════════════════════════════════╝");
    }).catch(err => {
      debugError("Failed to get WebRTC stats:", err);
    });
  }

  /**
   * Start video stream health monitoring
   */
  private startHealthMonitoring(): void {
    // Clear any existing interval
    if (this.healthMonitorInterval) {
      clearInterval(this.healthMonitorInterval);
    }

    debugLog("🏥 [AvatarService] Starting health monitoring");
    
    this.healthMonitorInterval = setInterval(() => {
      const video = document.getElementById(AVATAR_ELEMENT_IDS.AVATAR_VIDEO) as HTMLVideoElement;
      if (!video || !this.isReady) return;
      
      const currentTime = video.currentTime;
      
      // Check if video time is advancing (not hung)
      if (currentTime === this.lastVideoTime && currentTime > 0) {
        debugWarn("⚠️ [AvatarService] Video stream appears hung (time not advancing)");
        // Dispatch event for potential reconnection handling
        window.dispatchEvent(new CustomEvent('avatar-stream-hung'));
      }
      
      this.lastVideoTime = currentTime;
    }, 5000); // Check every 5 seconds
  }

  /**
   * Stop health monitoring
   */
  private stopHealthMonitoring(): void {
    if (this.healthMonitorInterval) {
      clearInterval(this.healthMonitorInterval);
      this.healthMonitorInterval = null;
      debugLog("🏥 [AvatarService] Health monitoring stopped");
    }
  }

  /**
   * Tests if AvatarSynthesizer can sync lips to pre-generated audio
    }

    try {
      debugLog("🔊 [AvatarService] Unmuting audio elements...");
      unmuteAllAudioElements();

      const avatarVideo = document.getElementById(
        AVATAR_ELEMENT_IDS.AVATAR_VIDEO
      ) as HTMLVideoElement;
      if (avatarVideo) {
        avatarVideo.muted = false;
        avatarVideo.volume = AZURE_AVATAR_CONFIG.AUDIO_VOLUME;
      }

      // Test 1: Try using AvatarSynthesizer's speakAudioAsync if available
      if (typeof (this.avatarSynthesizer as any).speakAudioAsync === 'function') {
        debugLog("✅ [AvatarService] Using AvatarSynthesizer.speakAudioAsync for lip-sync");
        const startTime = Date.now();
        await (this.avatarSynthesizer as any).speakAudioAsync(audioDataUrl);
        const duration = Date.now() - startTime;
        debugLog("✅ [AvatarService] Avatar audio playback completed with lip-sync:", { durationMs: duration });
        return;
      }

      // Test 2: Fallback to separate audio playback (NO lip-sync)
      debugLog("⚠️ [AvatarService] No speakAudioAsync - playing audio without lip-sync");
      const audio = new Audio(audioDataUrl);
      audio.volume = AZURE_AVATAR_CONFIG.AUDIO_VOLUME;
      
      debugLog("▶️ [AvatarService] Playing backend audio...");
      await audio.play();
      
      // Wait for audio to finish
      await new Promise<void>((resolve) => {
        audio.onended = () => {
          debugLog("✅ [AvatarService] Audio playback completed (no lip-sync)");
          resolve();
        };
        audio.onerror = (e) => {
          debugError("❌ [AvatarService] Audio playback error:", e);
          resolve();
        };
      });
    } catch (error) {
      debugError("❌ [AvatarService] Error in speakWithAudio():", error);
      throw error;
    }
  }

  /**
   * Stop avatar from speaking (interrupt current speech)
   */
  async stopSpeaking(): Promise<void> {
    try {
      if (
        this.avatarSynthesizer &&
        typeof this.avatarSynthesizer.stopSpeakingAsync === "function"
      ) {
        await this.avatarSynthesizer.stopSpeakingAsync();
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Complete session teardown
   */
  async stopAvatarSession(): Promise<void> {
    try {
      if (this.avatarSynthesizer) {
        await this.avatarSynthesizer.stopAvatarAsync();
        this.avatarSynthesizer = null;
      }

      if (this.peerConnection) {
        this.peerConnection.close();
        this.peerConnection = null;
      }

      this.isReady = false;
      this.isConnected = false;
      this.avatarSessionId = null;
      this.isPaused = false;
      this.pausedText = "";

      this.showStatus("", AVATAR_STATUS_TYPES.READY);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Pause avatar speaking
   */
  async pauseSpeaking(currentText: string = ""): Promise<void> {
    try {
      await this.stopSpeaking();
      this.isPaused = true;
      this.pausedText = currentText;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Resume avatar speaking
   */
  async resumeSpeaking(): Promise<void> {
    try {
      this.isPaused = false;

      const videoElement = document.getElementById(
        AVATAR_ELEMENT_IDS.AVATAR_VIDEO
      ) as HTMLVideoElement;
      if (videoElement && videoElement.paused) {
        videoElement.play();
      }

      if (this.pausedText) {
        const textToSpeak = this.pausedText;
        this.pausedText = "";
        await this.speak(textToSpeak);
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Show status message with visual feedback
   */
  private showStatus(
    message: string,
    type: "loading" | "ready" | "error"
  ): void {
    if (!this.avatarElement) return;

    const existingOverlay = this.avatarElement.querySelector(
      `.${AVATAR_CSS_CLASSES.STATUS_OVERLAY}`
    );
    if (existingOverlay) {
      existingOverlay.remove();
    }

    if (!message) return;

    const overlay = document.createElement("div");
    overlay.className = AVATAR_CSS_CLASSES.STATUS_OVERLAY;
    overlay.innerHTML = createStatusOverlayHTML(message, type);

    this.avatarElement.appendChild(overlay);
  }

  /**
   * Reinitialize the avatar connection for recovery
   */
  private async reinitializeAvatar(): Promise<void> {
    try {
      if (this.avatarSynthesizer) {
        this.avatarSynthesizer.close();
        this.avatarSynthesizer = null;
      }

      if (this.peerConnection) {
        this.peerConnection.close();
        this.peerConnection = null;
      }

      await new Promise((resolve) =>
        setTimeout(resolve, AZURE_AVATAR_CONFIG.REINITIALIZATION_DELAY_MS)
      );

      await this.startAvatarSession();

      this.showStatus("Avatar Ready", AVATAR_STATUS_TYPES.READY);

      window.dispatchEvent(
        new CustomEvent(SOCKET_EVENTS.AVATAR_READY, {
          detail: { ready: true },
        })
      );
    } catch (error) {
      this.showStatus("Reconnection failed", AVATAR_STATUS_TYPES.ERROR);
      throw error;
    }
  }
}

// Export singleton instance
export const azureCustomAvatarService = new AzureCustomAvatarService();
